import classNames from "classnames";
import { useState } from "react";
import SVG from "react-inlinesvg";
import styled from "styled-components/macro";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";
import { SudoTooltip } from "../../../../sudokit/components/SudoTooltip";
import ProgressContainer from "../../../ProgressContainer";
import StickyNoteAnchor from "../../../StickyTour/StickyNoteAnchor";
import { AnchorIds, TourSignal } from "../../../StickyTour/TourTypes";
import OutlinePicker from "../../OutlinePicker";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import { useGenerateOutline } from "./useGenerateOutline";

const ButtonContainer = styled.div`
  position: relative;
  .inner-progress-primary {
    display: flex;
    align-items: center;
    background-color: var(--primary-cta);
    height: 40px;
    border-radius: 10px;
    color: var(--cta-text-color);
    padding-left: 8px;
    svg {
      color: var(--cta-text-color);
      path {
        fill: var(--cta-text-color);
      }
      margin-right: 8px;
    }
    h1 {
      color: var(--cta-text-color);
    }
    h2 {
      color: var(--cta-text-color);
    }
    cursor: pointer;
  }

  .inner-progress-secondary {
    display: flex;
    align-items: center;
    background-color: var(--menu-color-fullscreen);
    height: 40px;
    border-radius: 10px;
    color: var(--grey-0);
    padding-left: 8px;
    svg {
      color: var(--grey-0);
      path {
        fill: var(--grey-0);
      }
      margin-right: 8px;
    }
    cursor: pointer;

    h1 {
      color: var(--grey-0);
    }
    h2 {
      color: var(--grey-0);
    }
  }
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 12px;
  justify-content: center;

  h1 {
    margin: 0 !important;
    font-size: 14px;
    color: #fff !important;
    font-weight: 500;
    line-height: 100%;
  }
  h2 {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 10px !important;
    color: #fff !important;
    font-weight: 600;
    letter-spacing: 0.5px;
    line-height: 130%;
    text-wrap: nowrap;
  }
`;

const PickerTriggerContainer = styled.div`
  border-left: 1px solid var(--primary-cta-divider);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 6px;
  padding-right: 9px;
  svg {
    margin-right: 0px !important;
  }
`;

const GenerateArea = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function GenerateButton({ projectId, primary, onGenerate }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { handleGenerate, isGenerateDisabled, isAgentGeneratingOutline, selectedOutline } = useGenerateOutline({ projectId, onGenerate });
  const readOnly = useStructuredOutlineStore((state) => state.readOnly);
  const setGenerationFramework = useStructuredOutlineStore((state) => state.setGenerationFramework);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  const handlePicker = (e) => {
    e.stopPropagation();
    setPickerOpen(!pickerOpen);
  };

  const handlePickerClose = () => {
    setPickerOpen(false);
  };
  const handleOutlineTypeSelection = (type) => {
    setGenerationFramework(type.value);
    setPickerOpen(false);
    sendFlowSignal(TourSignal.STORY_BIBLE_OUTLINE_TYPE_SELECTED);
  };

  const generateContent = (
    <GenerateArea className={classNames({ disabled: isGenerateDisabled })}>
      <SVG
        src="/assets/AI-sparkle.svg"
        aria-hidden="true"
        height={25}
        width={25}
      />
      <LabelContainer>
        <h1>Generate</h1>
        <h2>{selectedOutline.label}</h2>
      </LabelContainer>
    </GenerateArea>
  );

  return (
    <>
      {!pickerOpen && (
        <ButtonContainer
          onClick={handleGenerate}
          id="button-container-generate-outline"
        >
          <StickyNoteAnchor anchorId={AnchorIds.STORY_BIBLE_GENERATE_OUTLINE_BUTTON} />
          <ProgressContainer
            id="button-generate-outline"
            isLoading={readOnly || isAgentGeneratingOutline}
            className={classNames({ "inner-progress-primary": primary, "inner-progress-secondary": !primary })}
            duration={120}
          >
            {isGenerateDisabled ? (
              <SudoTooltip
                text="Select an outline type first"
                position="top"
              >
                {generateContent}
              </SudoTooltip>
            ) : (
              generateContent
            )}
            <PickerTriggerContainer onClick={handlePicker}>
              <SVG
                src="/assets/chevron-down.svg"
                aria-hidden="true"
                height={24}
                width={24}
              />
            </PickerTriggerContainer>
          </ProgressContainer>
        </ButtonContainer>
      )}
      {pickerOpen && (
        <OutlinePicker
          close={handlePickerClose}
          commitType={handleOutlineTypeSelection}
          currentType={selectedOutline}
        />
      )}
    </>
  );
}

export default GenerateButton;
